import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useTranslation } from '@/hooks/useTranslation';

const SORT_OPTIONS = [
  { id: 'featured', labelKey: 'explore.featured', icon: 'star', iosIcon: 'star' },
  { id: 'newest', labelKey: 'explore.newest', icon: 'clock', iosIcon: 'clock' },
  { id: 'popular', labelKey: 'explore.popular', icon: 'fire', iosIcon: 'flame' },
];

const FAVORITES_OPTION = {
  id: 'favorites',
  labelKey: 'pages.tools.myFavorites',
  fallbackLabel: 'My favorites',
  icon: 'heart',
  iosIcon: 'heart',
};

const FAVORITES_ACCENT = '#EC4899';

function ExploreSortDropdown({
  activeSort,
  filterFavorites = false,
  onChangeSort,
  variant = 'default',
  style,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const isChip = variant === 'toolbarChip';
  const triggerStyles = useMemo(
    () => [
      styles.trigger,
      isChip && styles.triggerChip,
      !isChip && isXS && styles.triggerCompact,
      style,
    ],
    [isChip, isXS, style],
  );

  const activeOption = filterFavorites
    ? FAVORITES_OPTION
    : (SORT_OPTIONS.find((s) => s.id === activeSort) || SORT_OPTIONS[0]);
  const activeIconColor = filterFavorites ? FAVORITES_ACCENT : colors.text_color_primary;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Pressable style={({ hovered }) => [
          ...triggerStyles,
          {
            backgroundColor: hovered ? colors.solid_color_secondary_dark : colors.solid_color_secondary,
            borderColor: colors.border_color_secondary,
          },
        ]}>
          <Icon
            name={activeOption.icon}
            size={13}
            color={activeIconColor}
            solid={filterFavorites}
          />
          <Text
            size={isChip ? 'xs' : (isXS ? 'xs' : 's')}
            weight="medium"
            numberOfLines={1}
            style={[
              { color: filterFavorites ? FAVORITES_ACCENT : colors.text_color_primary },
              isChip && styles.triggerChipLabel,
            ]}
          >
            {t(activeOption.labelKey) || activeOption.fallbackLabel}
          </Text>
          <Icon name="chevron-down" size={9} color={colors.text_color_tertiary} />
        </Pressable>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
        align={isChip ? 'center' : 'start'}
        sideOffset={4}
      >
        {Platform.OS === 'web' ? (
          <>
            {SORT_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={opt.id}
                icon={opt.icon}
                title={t(opt.labelKey)}
                isActive={!filterFavorites && activeSort === opt.id}
                onSelect={() => onChangeSort(opt.id)}
              />
            ))}
            <DropdownMenu.Separator className="DropdownMenuSeparator" />
            <DropdownMenuItem
              key={FAVORITES_OPTION.id}
              icon={FAVORITES_OPTION.icon}
              title={t(FAVORITES_OPTION.labelKey) || FAVORITES_OPTION.fallbackLabel}
              isActive={filterFavorites}
              onSelect={() => onChangeSort(FAVORITES_OPTION.id)}
            />
          </>
        ) : (
          <>
            {SORT_OPTIONS.map((opt) => (
              <DropdownMenu.Item key={opt.id} onSelect={() => onChangeSort(opt.id)}>
                <DropdownMenu.ItemIcon ios={{ name: opt.iosIcon }} />
                <DropdownMenu.ItemTitle>{t(opt.labelKey)}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            ))}
            <DropdownMenu.Separator />
            <DropdownMenu.Item
              key={FAVORITES_OPTION.id}
              onSelect={() => onChangeSort(FAVORITES_OPTION.id)}
            >
              <DropdownMenu.ItemIcon ios={{ name: FAVORITES_OPTION.iosIcon }} />
              <DropdownMenu.ItemTitle>
                {t(FAVORITES_OPTION.labelKey) || FAVORITES_OPTION.fallbackLabel}
              </DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          </>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    borderWidth: 1,
    flexShrink: 1,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  triggerCompact: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    gap: 5,
  },
  triggerChip: {
    flex: 1,
    minWidth: 0,
    width: '100%',
    height: 36,
    justifyContent: 'center',
    paddingHorizontal: 10,
    borderRadius: 10,
    backgroundColor: 'transparent',
  },
  triggerChipLabel: {
    flex: 1,
    minWidth: 0,
  },
});

export default React.memo(ExploreSortDropdown);
